/* eslint-disable @repo/internal/deprecations/deprecation-ticket-required */
import memoize from 'lodash/memoize';
import {
	type Action,
	type BoundActions,
	createActionsHook,
	createContainer,
	createHook,
	createSelector,
	createStateHook,
	createStore,
	type HookActionsFunction,
	type HookFunction,
	type HookStateFunction,
	type OverrideContainerComponent,
} from 'react-sweet-state';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import { type OnOAuthSuccessCallback } from '@atlassian/rovo-connectors-config/hooks/useRovoConfig';
import { type EntityATI } from '@atlassian/search-client/common/types';
import { type SearchCombinedUnitMetadata } from '@atlassian/search-client/config';
import {
	type TProductDataRegions as ProductDataRegions,
	type TProductEdition as ProductEdition,
} from '@atlassian/search-client/fetch-search-config';
import { type ThirdPartyFilters } from '@atlassian/search-client/query-generator';
import { type SmartlinkUsageAndTopApps } from '@atlassian/search-client/smartlink-usage-and-top-apps';
import { getBackendExperiment } from '@atlassian/search-experiment/backend';
import {
	type BackendExperiment,
	type ExperimentConfig,
	type FrontendExperiments,
} from '@atlassian/search-experiment/types';

import { ApplicationModes } from '../../../common/constants/application-modes';
import {
	type BootstrapState,
	type ClearReferrerCallback,
	type Features,
	initialState,
	type OnSearchCompletedCallback,
	type RefreshSearchSessionIdCallback,
	type SetQueryParamsCallback,
	type SetThirdPartyConfigsCallback,
} from '../../../common/constants/bootstrap';
import { SEARCH_PAGE } from '../../../common/constants/experience';
import { TypeFilterValueKey } from '../../../common/constants/filters/universal-type';
import type { NounKeys } from '../../../common/constants/nouns';
import { type PrimaryProductKey, ProductKeys } from '../../../common/constants/product-keys';
import type { BootstrapVariables } from '../../../common/constants/relay/types';
import type {
	ThirdPartyConfigsBootstrap,
	ThirdPartyConfigsWithAPIResponse,
} from '../../../common/constants/schemas/3p-config';
import { productKeySchema } from '../../../common/constants/schemas/product-key-schema';
import type {
	Overrides,
	SearchQueryStringParams,
	SearchQueryStringParamsRaw,
} from '../../../common/constants/schemas/query-params';
import {
	type UserDetailsWithPicture,
	type UserDetailsWithStatus,
} from '../../../common/constants/types';
import { getMultiSiteSearchInRovo } from '../../../common/utils/experiments';
import { getApplicationMode } from '../../../common/utils/get-application-mode';
import { getUser } from '../../../common/utils/get-user';
import { isHello } from '../../../common/utils/is-hello';
import {
	areAllEqualQueryParams,
	parseQueryParams,
	toQueryParamsRaw,
	toQueryString,
} from '../../../common/utils/query-params';

import {
	type HookReturn,
	useAggAbsoluteUrlContext,
	useAggregatorHostNameContext,
	useAllFilterExcludedProductsContext,
	useAnalyticsAttributesContext,
	useApplicationModeContext,
	useAutoSelectPrimaryProductContext,
	useAvailableNounsContext,
	useAvailableProductsContext,
	useBackendExperimentContext,
	useBootstrapContext,
	useBootstrapStateContext,
	useCloudIdContext,
	useCurrentUserIdContext,
	useExperimentConfigContext,
	useExperimentLayersContext,
	useFeaturesContext,
	useFiltersDiscoverability2EnabledContext,
	useFrontendExperimentsContext,
	useHasEditOrViewPlanPermissionContext,
	useInitialSpaceARIsContext,
	useInitialTextQueryParamContext,
	useIsAvailableProductsLoadedContext,
	useIsConfigLoadedContext,
	useIsHelloContext,
	useIsJiraIssueIndexedContext,
	useIsRovoEnabledContext,
	useIsRovoFreeContext,
	useIsTypeFilteredToIncidentContext,
	useLinkedCloudARIsContext,
	useMultiSiteSearchInRovoContext,
	useOnOAuthSuccessContext,
	useOnSearchCompletedContext,
	useOrgIdContext,
	usePrimaryProductContext,
	usePrimarySubProductContext,
	useProductDataRegionsContext,
	useProductEditionContext,
	useQueryParamsContext,
	useReferrerContext,
	useSAINHostNameContext,
	useSearchSessionIdContext,
	useSmartLinkUsageAndTopAppsContext,
	useSmartLinkUsageContext,
	useThirdPartyConfigsContext,
	useUnitMetadataContext,
	useUserContext,
} from './bootstrap-context-provider/accessors';
import {
	useClearReferrerContext,
	useRefreshSearchSessionIdContext,
	useSetQueryParamsContext,
	useSetThirdPartyConfigsContext,
} from './bootstrap-context-provider/actions';
import { serveBootstrapProviderExp } from './utils';

export const actions = {
	/**
	 * @deprecated
	 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
	 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
	 */
	setThirdPartyConfigs:
		(
			configs: Partial<ThirdPartyConfigsBootstrap>,
		): Action<BootstrapState, BootstrapContainerProps> =>
		({ setState }) => {
			setState({ thirdPartyConfigs: configs });
		},
	/**
	 * @deprecated
	 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
	 * Instead use `const setQueryParams = useSetQueryParams()`
	 */
	setQueryParams:
		(
			queryParam: Partial<SearchQueryStringParams>,
		): Action<BootstrapState, BootstrapContainerProps> =>
		({}, { queryParams, queryParamsCallback = () => {} }) => {
			const areEqualQueryParams = areAllEqualQueryParams(
				queryParam,
				parseQueryParams(queryParams ?? {}),
			);

			if (areEqualQueryParams) {
				return;
			}

			queryParamsCallback(toQueryParamsRaw(queryParam));
		},
	/**
	 * @deprecated
	 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
	 *
	 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
	 */
	syncQueryParams:
		(): Action<BootstrapState, BootstrapContainerProps> =>
		({ setState, getState }, { queryParams, queryParamsCallback = () => {} }) => {
			// queryParams is the query params coming from the URL or React Router in this case
			const {
				prevQueryParams, // Previously saved query params - this is to prevent infinitely updating queryParams
				queryParams: diffQueryParamsState, // The 'internal' query params state
			} = getState();

			const queryParamsURL = parseQueryParams(queryParams ?? {});

			const prevQueryParamsString = toQueryString(prevQueryParams);
			const diffQueryParamsURLString = toQueryString(queryParamsURL);
			const diffQueryParamsStateString = toQueryString(diffQueryParamsState);

			const allQueryParamsAreEqual = areAllEqualQueryParams(
				queryParamsURL,
				prevQueryParams,
				diffQueryParamsState,
			);

			if (allQueryParamsAreEqual) {
				return;
			}

			// Any changes from the url takes the highest precedence
			const hasURLQueryParams = !!queryParams;
			if (hasURLQueryParams && prevQueryParamsString !== diffQueryParamsURLString) {
				setState({ prevQueryParams: queryParamsURL, queryParams: queryParamsURL });
				return;
			}

			if (prevQueryParamsString !== diffQueryParamsStateString) {
				//  NOTE: If you are running the callback and you don't see the query params being updated,
				//  the callback might be sanitising the query parameters. This is the case for Confluence.
				queryParamsCallback(toQueryParamsRaw(diffQueryParamsState));
				setState({ prevQueryParams: diffQueryParamsState, queryParams: diffQueryParamsState });
				return;
			}
		},
	/**
	 * @deprecated
	 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
	 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
	 */
	refreshSearchSessionId:
		(): Action<BootstrapState, BootstrapContainerProps> =>
		({ setState }) => {
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			setState({ searchSessionId: uuid() });
		},
	/**
	 * @deprecated
	 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
	 * Instead use `const clearReferrer = useClearReferrer()`
	 */
	clearReferrer:
		(): Action<BootstrapState, BootstrapContainerProps> =>
		({ setState }) => {
			setState({ referrer: undefined });
		},
};

const buildProducts = memoize(
	(a: ProductKeys[], b: ProductKeys[]) => {
		return [...new Set([...a, ...b])];
	},
	(a: ProductKeys[], b: ProductKeys[]) => a.join('') + b.join(''),
);

type TActions = typeof actions;

// Keep store private and use hooks to access state and actions
const Store = createStore<BootstrapState, TActions>({ initialState, actions, name: 'bootstrap' });

export const bootstrapStoreContainerActions = {
	onInit:
		(): Action<BootstrapState, BootstrapContainerProps> =>
		({ getState, setState }, { initialProps, onSearchCompleted, onOAuthSuccess, queryParams }) => {
			const parsedQueryParams = queryParams && parseQueryParams(queryParams);
			setState({
				...getState(),
				...initialProps,
				...(queryParams && { queryParams: parsedQueryParams }),
				referrer: parsedQueryParams?.referrer,
				initialTextQueryParam: parsedQueryParams?.text,
				// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
				searchSessionId: initialProps.searchSessionId || uuid(),
				onSearchCompleted,
				onOAuthSuccess,
			});
		},
	onUpdate:
		(): Action<BootstrapState, BootstrapContainerProps> =>
		(
			{ setState, getState, dispatch },
			{ initialProps, onSearchCompleted, onOAuthSuccess, queryParams },
		) => {
			const parsedQueryParams = queryParams && parseQueryParams(queryParams);

			const currentState = getState();
			const products = buildProducts(currentState.products, initialProps?.products ?? []);

			// Merge thirdPartyConfigs so that OAuth-updated entries (set via
			// setThirdPartyConfigs with userNeedsOAuth: false) are not
			// overwritten by the stale consumer-provided value on re-renders,
			// while still picking up new/updated entries from the host app.
			//
			// IMPORTANT: We only create a new object when an actual override is
			// needed. If nothing changed, we preserve the incoming reference to
			// avoid unnecessary re-renders in RSS store subscribers (e.g.
			// useFetchResultsProps depends on thirdPartyConfigs by reference).
			const incomingThirdPartyConfigs = initialProps?.thirdPartyConfigs;
			const currentThirdPartyConfigs = currentState.thirdPartyConfigs;

			let mergedThirdPartyConfigs = incomingThirdPartyConfigs;
			if (incomingThirdPartyConfigs && currentThirdPartyConfigs) {
				// Check whether any entries need to be preserved from the current store
				let hasOverrides = false;
				for (const key of Object.keys(currentThirdPartyConfigs)) {
					const currentEntry =
						currentThirdPartyConfigs[key as keyof typeof currentThirdPartyConfigs];
					const incomingEntry =
						incomingThirdPartyConfigs[key as keyof typeof incomingThirdPartyConfigs];
					if (
						currentEntry &&
						incomingEntry &&
						currentEntry.userNeedsOAuth === false &&
						incomingEntry.userNeedsOAuth === true
					) {
						hasOverrides = true;
						break;
					}
				}
				// Only create a new object if we actually need to override something
				if (hasOverrides) {
					mergedThirdPartyConfigs = { ...incomingThirdPartyConfigs };
					for (const key of Object.keys(currentThirdPartyConfigs)) {
						const currentEntry =
							currentThirdPartyConfigs[key as keyof typeof currentThirdPartyConfigs];
						const incomingEntry = (mergedThirdPartyConfigs as Record<string, unknown>)[key] as
							| typeof currentEntry
							| undefined;
						if (
							currentEntry &&
							incomingEntry &&
							currentEntry.userNeedsOAuth === false &&
							incomingEntry.userNeedsOAuth === true
						) {
							(mergedThirdPartyConfigs as Record<string, unknown>)[key] = currentEntry;
						}
					}
				}
			}

			setState({
				...currentState,
				...initialProps,
				...(mergedThirdPartyConfigs !== undefined && {
					thirdPartyConfigs: mergedThirdPartyConfigs,
				}),
				onSearchCompleted,
				onOAuthSuccess,
				products,
				referrer: parsedQueryParams?.referrer,
			});

			dispatch(actions.syncQueryParams());
		},
};

export type QueryParamsCallback = (queryParams: Partial<SearchQueryStringParamsRaw>) => void;

export type BootstrapContainerProps = {
	initialProps: Partial<BootstrapState>;
	queryParams?: Record<string, any>;
	/** Called when FPS query params should be updated in the application's router. */
	queryParamsCallback?: QueryParamsCallback;
	/**
	 * @private
	 * @deprecated this prop is deprecated and not used anymore. It will be removed soon.
	 *
	 * @returns the space IDs for the given space keys. NOTE: these should be the space IDs, not ARIs.
	 * @example getSpaceIdsFromSpaceKeys(["STATUS"]); // Promise<["123454321"]>
	 **/
	getSpaceIdsFromSpaceKeys?: (spaceKeys: string[]) => Promise<string[]>;
	/** Called when the Quick Find search completes successfully. */
	onSearchCompleted?: OnSearchCompletedCallback;
	/** Called when a 3P connector has just completed OAuth locally. */
	onOAuthSuccess?: OnOAuthSuccessCallback;
};

export const BootstrapStoreContainer: OverrideContainerComponent<BootstrapContainerProps> =
	createContainer<BootstrapState, TActions, BootstrapContainerProps>(
		Store,
		bootstrapStoreContainerActions,
	);

// Hooks

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * This is being actively migrated away from in QS-9194
 *
 * Instead use specific per-action accessors from this file if they are present
 *
 * e.g. const { setQueryParams } = useBootstrapActions();
 * -> const setQueryParams = useSetQueryParams();
 */
export const useBootstrapActions: HookActionsFunction<
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>
> = createActionsHook(Store);

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * This is being actively migrated away from in QS-9194
 *
 * Instead use specific per-value accessors from this file, including for actions if they are present
 *
 * e.g. const [{ useCloudId, useQueryParams }, { useSetQueryParams }] = useBootstrap()
 * -> const [cloudId] = useCloudId();
 * -> const [queryParams] = useQueryParams();
 * -> const setQueryParams = useSetQueryParams();
 */
const useBootstrapStore = createHook(Store);

export const useBootstrap = (): HookReturn<BootstrapState> => {
	const serveContext = serveBootstrapProviderExp();

	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useBootstrapContext()
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useBootstrapStore()[0]] satisfies HookReturn<BootstrapState>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useBootstrapState in bootstrap/index.tsx
 */
export const useBootstrapStateStore = createStateHook(Store, {
	selector: (state: BootstrapState, cb: (state: BootstrapState) => unknown) => cb(state),
}) as unknown as <T>(cb: (state: BootstrapState) => T) => T;

/**
 * Retrieve BootstrapVariables (with applied callback) from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 * @deprecated Prefer accessing values from the store directly with their individual helpers
 */
export const useBootstrapState = (
	cb: (state: BootstrapVariables) => BootstrapVariables,
): BootstrapVariables => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useBootstrapStateContext(cb) : useBootstrapStateStore(cb);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAnalyticsAttributes in bootstrap/index.tsx
 *
 * This hook gets the extra analytics attributes that are passed in as a prop from the consumer.
 * It should not be confused with the hooks for firing analytics events in `controllers/use-analytics`
 */
export const useAnalyticsAttributesStore: HookFunction<
	Record<string, any> | undefined,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.analyticsAttributes,
});

/**
 * Retrieve analytics attributes from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAnalyticsAttributes = (): HookReturn<Record<string, any> | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useAnalyticsAttributesContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useAnalyticsAttributesStore()[0]] satisfies HookReturn<Record<string, any> | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAvailableProducts in bootstrap/index.tsx
 */
export const useAvailableProductsStore: HookStateFunction<string[], void> = createStateHook(Store, {
	selector: (state) => state.products,
});

/**
 * Retrieve available products from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAvailableProducts = (): ProductKeys[] => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useAvailableProductsContext() : useAvailableProductsStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAvailableNouns in bootstrap/index.tsx
 */
export const useAvailableNounsStore: HookStateFunction<NounKeys[], void> = createStateHook(Store, {
	selector: (state) => state.nouns,
});

/**
 * Retrieve available nouns from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAvailableNouns = (): NounKeys[] => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useAvailableNounsContext() : useAvailableNounsStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsAvailableProductsLoaded in bootstrap/index.tsx
 */
export const useIsAvailableProductsLoadedStore: HookStateFunction<boolean, void> = createStateHook(
	Store,
	{
		selector: (state) => state.products && state.products.length > 0,
	},
);

/**
 * Retrieve whether available products have been loaded from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIsAvailableProductsLoaded = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useIsAvailableProductsLoadedContext() : useIsAvailableProductsLoadedStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useBootstrapLoading in bootstrap/index.tsx
 */
export const useBootstrapLoadingStore: HookFunction<
	boolean,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.products.length === 0,
});

/**
 * Retrieve whether bootstrap is still loading (products not yet available) from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useBootstrapLoading = (): HookReturn<boolean> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[!useIsAvailableProductsLoadedContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useBootstrapLoadingStore()[0]] satisfies HookReturn<boolean>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use usePrimaryProduct in bootstrap/index.tsx
 */
export const usePrimaryProductStore: HookFunction<
	PrimaryProductKey | undefined,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.primaryProduct,
});

/**
 * Retrieve primaryProduct from the store. PrimaryProduct identifies the main product context.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const usePrimaryProduct = (): HookReturn<PrimaryProductKey | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[usePrimaryProductContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([usePrimaryProductStore()[0]] satisfies HookReturn<PrimaryProductKey | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use usePrimarySubProduct in bootstrap/index.tsx
 */
const usePrimarySubProductStore = createStateHook(Store, {
	selector: (state) => state.primarySubProduct,
});

/**
 * Retrieve primarySubProduct from the store. PrimarySubProduct identifies a sub-experience within
 * the host primary product (e.g. for Jira: `software`, `service-desk`, `core`,
 * `polaris`, `customer-service`). Used to enrich cross-product analytics so the
 * emitted `interactionSource` becomes `${primaryProduct}-${primarySubProduct}` when set.
 *
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const usePrimarySubProduct = (): HookReturn<string | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[usePrimarySubProductContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([usePrimarySubProductStore()] satisfies HookReturn<string | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useSearchSessionId in bootstrap/index.tsx
 */
export const useSearchSessionIdStore: HookFunction<
	string,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.searchSessionId,
});

/**
 * Retrieve the search session ID from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useSearchSessionId = (): HookReturn<string> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useSearchSessionIdContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useSearchSessionIdStore()[0]] satisfies HookReturn<string>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsHello in bootstrap/index.tsx
 */
export const useIsHelloStore: HookFunction<
	boolean,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, { selector: (state) => isHello(state.cloudId) });

/**
 * Retrieve whether the current tenant is Hello from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIsHello = (): HookReturn<boolean> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useIsHelloContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useIsHelloStore()[0]] satisfies HookReturn<boolean>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useFeatures in bootstrap/index.tsx
 */
export const useFeaturesStore: HookFunction<
	Features | undefined,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, { selector: (state) => state.features });

/**
 * Retrieve features from the store. Features are defined by the host product.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useFeatures = (): HookReturn<Features | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useFeaturesContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useFeaturesStore()[0]] satisfies HookReturn<Features | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useBackendExperiment in bootstrap/index.tsx
 */
export const useBackendExperimentStore: HookFunction<
	BackendExperiment,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	{
		entities: EntityATI[];
		thirdPartyFilters: ThirdPartyFilters;
	}
> = createHook(Store, {
	selector: (state, args: { entities: EntityATI[]; thirdPartyFilters: ThirdPartyFilters }) => {
		return getBackendExperiment({
			experimentConfig: state.experimentConfig,
			entities: args.entities,
			productEdition: state.productEdition,
			isHello: isHello(state.cloudId),
			productDataRegions: state.productDataRegions,
			experience: SEARCH_PAGE,
			tenantId: state.cloudId,
			isJiraIssueIndexed: state.isJiraIssueIndexed,
			thirdPartyFilters: args.thirdPartyFilters,
		});
	},
});

/**
 * Retrieve the backend experiment from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useBackendExperiment = (args: {
	entities: EntityATI[];
	thirdPartyFilters: ThirdPartyFilters;
}): HookReturn<BackendExperiment> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useBackendExperimentContext(args)]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useBackendExperimentStore(args)[0]] satisfies HookReturn<BackendExperiment>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useFrontendExperiments in bootstrap/index.tsx
 */
export const useFrontendExperimentsStore: HookFunction<
	FrontendExperiments,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.experimentConfig.frontendExperiments,
});

/**
 * Retrieve frontend experiments from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useFrontendExperiments = (): HookReturn<FrontendExperiments> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useFrontendExperimentsContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useFrontendExperimentsStore()[0]] satisfies HookReturn<FrontendExperiments>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useSmartLinkUsage in bootstrap/index.tsx
 */
export const useSmartLinkUsageStore: HookStateFunction<Record<string, number> | undefined, void> =
	createStateHook(Store, {
		selector: (state) => state.smartLinkUsage,
	});

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useUnitMetadata in bootstrap/index.tsx
 */
export const useUnitMetadataStore: HookStateFunction<
	SearchCombinedUnitMetadata[] | undefined,
	void
> = createStateHook(Store, {
	selector: (state) => state.unitMetadata,
});

/**
 * Retrieve Smart Link usage data from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useSmartLinkUsage = (): Record<string, number> | undefined => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useSmartLinkUsageContext() : useSmartLinkUsageStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useSmartLinkUsageAndTopApps in bootstrap/index.tsx
 */
export const useSmartLinkUsageAndTopAppsStore: HookStateFunction<
	SmartlinkUsageAndTopApps | undefined,
	void
> = createStateHook(Store, {
	selector: (state) => state.smartLinkUsageAndTopApps,
});

/**
 * Retrieve site-scoped Smart Link usage % and top apps trait from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useSmartLinkUsageAndTopApps = (): SmartlinkUsageAndTopApps | undefined => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useSmartLinkUsageAndTopAppsContext() : useSmartLinkUsageAndTopAppsStore();
};

export const useMultiSiteSearchInRovoStore: HookStateFunction<boolean, void> = createStateHook(
	Store,
	{
		selector: (state) => getMultiSiteSearchInRovo(state.experimentConfig),
	},
);

/**
 * Retrieve whether multi-site search in Rovo is enabled from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useMultiSiteSearchInRovo = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useMultiSiteSearchInRovoContext() : useMultiSiteSearchInRovoStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useCurrentUserId in bootstrap/index.tsx
 */
export const useCurrentUserIdStore: HookStateFunction<string | null | undefined, void> =
	createStateHook(Store, {
		selector: (state): string | null | undefined => state.user?.id,
	});

/**
 * Retrieve the current user ID from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useCurrentUserId = (): string | null | undefined => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useCurrentUserIdContext() : useCurrentUserIdStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useExperimentConfig in bootstrap/index.tsx
 */
export const useExperimentConfigStore: HookFunction<
	ExperimentConfig,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.experimentConfig,
});

/**
 * Retrieve the experiment config from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useExperimentConfig = (): HookReturn<ExperimentConfig> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useExperimentConfigContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useExperimentConfigStore()[0]] satisfies HookReturn<ExperimentConfig>);
};

/**
 * @deprecated
 * https://hello.atlassian.net/wiki/x/bKRdZAE
 */
const useApplicationModeStore = createStateHook(Store, {
	selector: createSelector(
		[(state) => state.isRovoEnabled],
		(isRovoEnabled): ApplicationModes => getApplicationMode({ isRovoEnabled }),
	),
});

/**
 * Retrieve the current application mode (Rovo or Unified).
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useApplicationMode = (): ApplicationModes => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useApplicationModeContext() : useApplicationModeStore();
};

/**
 * Retrieve linked cloud ARIs.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useLinkedCloudARIs = (): [string[] | undefined] => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? [useLinkedCloudARIsContext()] : [useLinkedCloudARIsStore()[0]];
};

/**
 * Retrieve the tenant's collaboration-context (unit) site metadata.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useUnitMetadata = (): SearchCombinedUnitMetadata[] | undefined => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useUnitMetadataContext() : useUnitMetadataStore();
};

/**
 * Retrieve the initial text query param.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useInitialTextQueryParam = (): string | undefined => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useInitialTextQueryParamContext() : useInitialTextQueryParamStore();
};

/**
 * Retrieve the referrer.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useReferrer = (): [string | undefined] => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? [useReferrerContext()] : [useReferrerStore()[0]];
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useUser in bootstrap/index.tsx
 */
export const useUserStore: HookFunction<
	UserDetailsWithStatus,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state): UserDetailsWithStatus => {
		const user = state.user satisfies UserDetailsWithPicture | undefined;
		return getUser(user);
	},
});

/**
 * Retrieve user details from the store with authentication status.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useUser = (): HookReturn<UserDetailsWithStatus> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useUserContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useUserStore()[0]] satisfies HookReturn<UserDetailsWithStatus>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useReferrer in bootstrap/index.tsx
 */
export const useReferrerStore: HookFunction<
	string | undefined,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, { selector: (state) => state.referrer });

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useInitialTextQueryParam in bootstrap/index.tsx
 */
export const useInitialTextQueryParamStore: HookStateFunction<string | undefined, void> =
	createStateHook(Store, {
		selector: (state) => state.initialTextQueryParam,
	});

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useOrgId in bootstrap/index.tsx
 */
export const useOrgIdStore: HookFunction<
	string | undefined,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, { selector: (state) => state.orgId });

/**
 * Retrieve orgId from the store. OrgId identifies the Atlassian organization.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useOrgId = (): HookReturn<string | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useOrgIdContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useOrgIdStore()[0]] satisfies HookReturn<string | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useCloudId in bootstrap/index.tsx
 */
export const useCloudIdStore: HookFunction<
	string | undefined,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: createSelector([(state) => state.cloudId], (cloudId) => cloudId),
});

/**
 * Retrieve cloudId from the store. CloudId identifies the Atlassian cloud instance.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useCloudId = (): HookReturn<string | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useCloudIdContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useCloudIdStore()[0]] satisfies HookReturn<string | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useLinkedCloudARIs in bootstrap/index.tsx
 */
export const useLinkedCloudARIsStore: HookFunction<
	string[] | undefined,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.linkedCloudARIs,
});

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAutoSelectPrimaryProduct in bootstrap/index.tsx
 */
export const useAutoSelectPrimaryProductStore: HookStateFunction<string | null, void> =
	createStateHook(Store, {
		selector: createSelector(
			[
				(state) => state.products,
				(state) => state.primaryProduct,
				(state) => state.isRovoEnabled,
				(state) => state.queryParams.product,
				(state) => state.queryParams.cql,
			],
			(
				products,
				primaryProduct,
				isRovoEnabled,
				currentSelectedProduct,
				currentCql,
			): ProductKeys | null => {
				const applicationMode = getApplicationMode({ isRovoEnabled });
				const primaryProductToSelect = productKeySchema.safeParse(primaryProduct);

				const shouldAutoSelectPrimaryProduct =
					!currentSelectedProduct &&
					!currentCql &&
					products &&
					products.length > 0 &&
					applicationMode === ApplicationModes.Unified &&
					primaryProductToSelect.success &&
					primaryProductToSelect.data !== ProductKeys.Atlas;

				if (shouldAutoSelectPrimaryProduct) {
					return primaryProductToSelect.data;
				} else {
					return null;
				}
			},
		),
	});

/**
 * Retrieve whether the primary product should be auto-selected from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAutoSelectPrimaryProduct = (): ProductKeys | null => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useAutoSelectPrimaryProductContext() : useAutoSelectPrimaryProductStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsConfigLoaded in bootstrap/index.tsx
 */
export const useIsConfigLoadedStore: HookStateFunction<boolean, void> = createStateHook(Store, {
	selector: createSelector([(state) => state.isConfigLoaded], (isConfigLoaded) => isConfigLoaded),
});

/**
 * Retrieve whether the bootstrap config has finished loading from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIsConfigLoaded = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useIsConfigLoadedContext() : useIsConfigLoadedStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsRovoFree in bootstrap/index.tsx
 * Note: this hook is only temporary and should be removed once Rovo is enabled for all users
 */
export const useIsRovoFreeStore: HookFunction<
	boolean,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => !!state.rovoFeatures?.scas,
});

/**
 * Retrieve whether Rovo is free from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 * Note: this hook is only temporary and should be removed once Rovo is enabled for all users
 */
export const useIsRovoFree = (): HookReturn<boolean> => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useIsRovoFreeContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useIsRovoFreeStore()[0]] satisfies HookReturn<boolean>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useProductEdition in bootstrap/index.tsx
 */
export const useProductEditionStore: HookFunction<
	ProductEdition | undefined,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.productEdition,
});

/**
 * Retrieve product edition from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useProductEdition = (): (ProductEdition | undefined)[] => {
	const serveContext = serveBootstrapProviderExp();
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useProductEditionContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useProductEditionStore()[0]] satisfies (ProductEdition | undefined)[]);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsTypeFilteredToIncident in bootstrap/index.tsx
 */
export const useIsTypeFilteredToIncidentStore: HookStateFunction<boolean, void> = createStateHook(
	Store,
	{
		selector: (state): boolean => state.queryParams?.type === TypeFilterValueKey.Incident,
	},
);

/**
 * Retrieve whether the type filter is set to Incident from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIsTypeFilteredToIncident = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext ? useIsTypeFilteredToIncidentContext() : useIsTypeFilteredToIncidentStore();
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useThirdPartyConfigs in bootstrap/index.tsx
 */
export const useThirdPartyConfigsStore: HookFunction<
	Partial<Record<string, ThirdPartyConfigsWithAPIResponse>>,
	BoundActions<
		BootstrapState,
		{
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setThirdPartyConfigs = useSetThirdPartyConfigs()`
			 */
			setThirdPartyConfigs: (
				configs: Partial<ThirdPartyConfigsBootstrap>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const setQueryParams = useSetQueryParams()`
			 */
			setQueryParams: (
				queryParam: Partial<SearchQueryStringParams>,
			) => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 *
			 * You should not ever need to call this directly. Open a thread in #help-search-plex and tag @rwasher, @sogrady, @jquach, if you find you need to!
			 */
			syncQueryParams: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const refreshSearchSessionId = useRefreshSearchSessionId()`
			 */
			refreshSearchSessionId: () => Action<BootstrapState, BootstrapContainerProps>;
			/**
			 * @deprecated
			 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
			 * Instead use `const clearReferrer = useClearReferrer()`
			 */
			clearReferrer: () => Action<BootstrapState, BootstrapContainerProps>;
		}
	>,
	void
> = createHook(Store, {
	selector: (state) => state.thirdPartyConfigs,
});

/**
 * Retrieve third party configs from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useThirdPartyConfigs = (): HookReturn<ThirdPartyConfigsBootstrap> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useThirdPartyConfigsContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useThirdPartyConfigsStore()[0]] satisfies HookReturn<ThirdPartyConfigsBootstrap>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useFiltersDiscoverability2Enabled in bootstrap/index.tsx
 */
export const useFiltersDiscoverability2EnabledStore: HookStateFunction<boolean, void> =
	createStateHook(Store, {
		selector: (state) => {
			const primaryProduct = state.primaryProduct;
			if (!primaryProduct) {
				return false;
			}

			return (
				primaryProduct === ProductKeys.Confluence ||
				primaryProduct === ProductKeys.Jira ||
				primaryProduct === ProductKeys.Atlas
			);
		},
	});

/**
 * Retrieve whether filters discoverability 2 is enabled from context or store.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useFiltersDiscoverability2Enabled = (): boolean => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useFiltersDiscoverability2EnabledContext()
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useFiltersDiscoverability2EnabledStore();
};

/**
 * Retrieve the callback to set query parameters into the URL (to commit a filter selection). Accepts the new parameter to set and merges with the current query parameters.
 *
 * The callback is expected to update the query parameters in the URL first, and then the URL change propagates back to FPS where it triggers a store update accordingly.
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useSetQueryParams = (queryParams: SearchQueryStringParams): SetQueryParamsCallback => {
	const serveContext = serveBootstrapProviderExp();

	const { setQueryParams: setQueryParamsStore } = useBootstrapActions();
	const setQueryParamsContext = useSetQueryParamsContext(queryParams);

	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			setQueryParamsContext
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			setQueryParamsStore;
};

/**
 * Retrieve the callback to refresh the search session ID. This is used when we want to generate a new search session ID without remounting the entire bootstrap provider, such as when the user clicks on the "Search" button in Quick Find. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useRefreshSearchSessionId = (): RefreshSearchSessionIdCallback => {
	const serveContext = serveBootstrapProviderExp();

	const { refreshSearchSessionId: refreshSearchSessionIdStore } = useBootstrapActions();
	const refreshSearchSessionIdContext = useRefreshSearchSessionIdContext();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			refreshSearchSessionIdContext
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			refreshSearchSessionIdStore;
};

export const useClearReferrer = (): ClearReferrerCallback => {
	const serveContext = serveBootstrapProviderExp();

	const { clearReferrer: clearReferrerStore } = useBootstrapActions();
	const clearReferrerContext = useClearReferrerContext();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			clearReferrerContext
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			clearReferrerStore;
};

/**
 * Retrieve the callback to set third party configs. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useSetThirdPartyConfigs = (): SetThirdPartyConfigsCallback => {
	const serveContext = serveBootstrapProviderExp();

	const { setThirdPartyConfigs: setThirdPartyConfigsStore } = useBootstrapActions();
	const setThirdPartyConfigsContext = useSetThirdPartyConfigsContext();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			setThirdPartyConfigsContext
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			setThirdPartyConfigsStore;
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useOnSearchCompleted in bootstrap/index.tsx
 */
export const useOnSearchCompletedStore: HookStateFunction<
	OnSearchCompletedCallback | undefined,
	void
> = createStateHook(Store, {
	selector: (state) => state.onSearchCompleted,
});

/**
 * Retrieve the callback to be called when a search is completed. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useOnSearchCompleted = (): OnSearchCompletedCallback => {
	const serveContext = serveBootstrapProviderExp();

	const onSearchCompletedStore = useOnSearchCompletedStore();
	const onSearchCompletedContext = useOnSearchCompletedContext();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			onSearchCompletedContext
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			(onSearchCompletedStore ?? (() => {}));
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useOnOAuthSuccess in bootstrap/index.tsx
 */
export const useOnOAuthSuccessStore: HookStateFunction<OnOAuthSuccessCallback | undefined, void> =
	createStateHook(Store, {
		selector: (state) => state.onOAuthSuccess,
	});

/**
 * Retrieve the OAuth-success callback surfaced by useRovoConfig. Uses context
 * when `serveBootstrapProviderExp` is enabled, otherwise falls back to store.
 *
 * When the `rovo_fps_use_rovo_config` gate is OFF this returns `undefined` and
 * callers should fall back to the legacy refresh path.
 */
export const useOnOAuthSuccess = (): OnOAuthSuccessCallback | undefined => {
	const serveContext = serveBootstrapProviderExp();

	const onOAuthSuccessStore = useOnOAuthSuccessStore();
	const onOAuthSuccessContext = useOnOAuthSuccessContext();

	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			onOAuthSuccessContext
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			onOAuthSuccessStore;
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useQueryParams in bootstrap/index.tsx
 */
export const useQueryParamsStore: HookStateFunction<
	{
		cql?: string | undefined;
		archived?: boolean | undefined;
		createdBy?: string[] | undefined;
		verified?: boolean | undefined;
		focusAreaUnder?: string[] | undefined;
		pageStatus?: string | undefined;
		lastUpdatedBy?: string[] | undefined;
		closeDate?: string | undefined;
		closeDateVal?: string | undefined;
		opportunityStatus?: string[] | undefined;
		compassArchived?: boolean | undefined;
		assetsSchema?: string[] | undefined;
		assetsType?: string[] | undefined;
		type?: string | undefined;
		text?: string | undefined;
		from?: string | undefined;
		to?: string | undefined;
		status?: string[] | undefined;
		spaces?: string[] | undefined;
		product?: string | undefined;
		originalQuery?: string | undefined;
		datasource?: string | undefined;
		filterDropdownOpen?: boolean | undefined;
		lastModified?:
			| 'custom'
			| 'anytime'
			| 'today'
			| 'yesterday'
			| 'past7Days'
			| 'past30Days'
			| 'pastYear'
			| undefined;
		lastModified_lt?: Date | undefined;
		lastModified_gt?: Date | undefined;
		sort?:
			| 'relevance'
			| 'updatedNewest'
			| 'updatedOldest'
			| 'createdNewest'
			| 'createdOldest'
			| undefined;
		titleMatch?: boolean | undefined;
		contributors?: string[] | undefined;
		owners?: string[] | undefined;
		ancestor?: string | undefined;
		salesforceStages?: string[] | undefined;
		labels?: string[] | undefined;
		jAssignee?: string[] | undefined;
		jReporter?: string[] | undefined;
		jCommentedByMe?: boolean | undefined;
		jProject?: string[] | undefined;
		jWatchedByMe?: boolean | undefined;
		focusAreaTypes?: string[] | undefined;
		positionEmploymentTypes?: string[] | undefined;
		positionFocusAreas?: string[] | undefined;
		positionJobFamilies?: string[] | undefined;
		positionStatuses?: string[] | undefined;
		positionTeams?: string[] | undefined;
		referrer?: string | undefined;
		overrides?: Overrides;
	},
	void
> = createStateHook(Store, {
	selector: (state) => state.queryParams,
});

/**
 * Retrieve query params. QueryParams change as the user interacts with search (input, filters).
 * Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useQueryParams = (): HookReturn<SearchQueryStringParams> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useQueryParamsContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useQueryParamsStore()] satisfies HookReturn<SearchQueryStringParams>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAggregatorHostName in bootstrap/index.tsx
 */
export const useAggregatorHostNameStore: HookStateFunction<string | undefined, void> =
	createStateHook(Store, {
		selector: (state) => state.aggregatorHostName,
	});

/**
 * Retrieve the aggregator host name. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAggregatorHostName = (): HookReturn<string | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useAggregatorHostNameContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useAggregatorHostNameStore()] satisfies HookReturn<string | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAggAbsoluteUrl in bootstrap/index.tsx
 */
export const useAggAbsoluteUrlStore: HookStateFunction<string | undefined, void> = createStateHook(
	Store,
	{
		selector: (state) => state.aggAbsoluteUrl,
	},
);

/**
 * Retrieve the aggregator absolute URL. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAggAbsoluteUrl = (): HookReturn<string | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useAggAbsoluteUrlContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useAggAbsoluteUrlStore()] satisfies HookReturn<string | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useAllFilterExcludedProducts in bootstrap/index.tsx
 */
export const useAllFilterExcludedProductsStore: HookStateFunction<string[], void> = createStateHook(
	Store,
	{
		selector: (state) => state.allFilterExcludedProducts,
	},
);

/**
 * Retrieve all filter excluded products. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useAllFilterExcludedProducts = (): HookReturn<ProductKeys[]> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useAllFilterExcludedProductsContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useAllFilterExcludedProductsStore()] satisfies HookReturn<ProductKeys[]>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useProductDataRegions in bootstrap/index.tsx
 */
export const useProductDataRegionsStore: HookStateFunction<ProductDataRegions | undefined, void> =
	createStateHook(Store, {
		selector: (state) => state.productDataRegions,
	});

/**
 * Retrieve product data regions. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useProductDataRegions = (): HookReturn<ProductDataRegions | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useProductDataRegionsContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useProductDataRegionsStore()] satisfies HookReturn<ProductDataRegions | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useIsJiraIssueIndexed in bootstrap/index.tsx
 */
export const useIsJiraIssueIndexedStore: HookStateFunction<boolean, void> = createStateHook(Store, {
	selector: (state) => state.isJiraIssueIndexed,
});

/**
 * Retrieve whether Jira issues are indexed. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useIsJiraIssueIndexed = (): HookReturn<boolean> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useIsJiraIssueIndexedContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useIsJiraIssueIndexedStore()] satisfies HookReturn<boolean>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useInitialSpaceARIs in bootstrap/index.tsx
 */
export const useInitialSpaceARIsStore: HookStateFunction<string[], void> = createStateHook(Store, {
	selector: (state) => state.initialSpaceARIs,
});

/**
 * Retrieve initial space ARIs. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useInitialSpaceARIs = (): HookReturn<string[]> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useInitialSpaceARIsContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useInitialSpaceARIsStore()] satisfies HookReturn<string[]>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useSAINHostName in bootstrap/index.tsx
 */
export const useSAINHostNameStore: HookStateFunction<string | undefined, void> = createStateHook(
	Store,
	{
		selector: (state) => state.SAINHostName,
	},
);

/**
 * Retrieve the SAIN host name. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useSAINHostName = (): HookReturn<string | undefined> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useSAINHostNameContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useSAINHostNameStore()] satisfies HookReturn<string | undefined>);
};

/**
 * @deprecated
 * Do not import directly! https://hello.atlassian.net/wiki/x/bKRdZAE
 * Instead use useHasEditOrViewPlanPermission in bootstrap/index.tsx
 */
export const useHasEditOrViewPlanPermissionStore: HookStateFunction<boolean | undefined, void> =
	createStateHook(Store, {
		selector: (state) => state.hasEditOrViewPlanPermission,
	});

/**
 * Retrieve whether the user has edit or view plan permission. Uses context when serveBootstrapProviderExp is enabled, otherwise falls back to store.
 */
export const useHasEditOrViewPlanPermission = (): HookReturn<boolean> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useHasEditOrViewPlanPermissionContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useHasEditOrViewPlanPermissionStore() ?? false] satisfies HookReturn<boolean>);
};

const useIsRovoEnabledStore = createStateHook(Store, {
	selector: (state): boolean => state.isRovoEnabled,
});

/**
 * Retrieve whether Rovo is enabled.
 */
export const useIsRovoEnabled = (): HookReturn<boolean> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useIsRovoEnabledContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useIsRovoEnabledStore()] satisfies HookReturn<boolean>);
};

const useExperimentLayersStore = createStateHook(Store, {
	selector: (state): string[] => state.experimentLayers,
});

/**
 * Retrieve experiment layers.
 */
export const useExperimentLayers = (): HookReturn<string[]> => {
	const serveContext = serveBootstrapProviderExp();
	// eslint-disable-next-line react-hooks/rules-of-hooks
	return serveContext
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			[useExperimentLayersContext()]
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			([useExperimentLayersStore()] satisfies HookReturn<string[]>);
};
